import React from 'react'
import { Button, Input, Modal, Skeleton, Switch, Typography } from 'antd'
import { sellerApi } from 'api/seller'
import {
  ESellerDisableReason,
  IAdminSellerActivity,
  IAdminSellerSettings,
  ISeller
} from 'models/seller'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import {
  ORDER_CONFIRMATION_DEADLINE_HOURS,
  ORDER_SHIPPING_DEADLINE_HOURS
} from 'shared/utils/orders/getOrderDeadline'
import { SectionTitle } from 'components/ui'
import { SellerActivity } from 'components/sellers/SellerActivity/SellerActivity'
import { SellerCommission } from 'components/sellers/SellerCommission/SellerCommission'
import { SellerCancelPenalty } from 'components/sellers/SellerCancelPenalty/SellerCancelPenalty'
import { SellerCommissionDiscount } from 'components/sellers/SellerCommissionDiscount/SellerCommissionDiscount'
import styles from './SellerSettings.module.scss'

interface ISellerSettingsProps {
  sellerId: string
}

const MIN_DEADLINE_HOURS = 1
const MAX_DEADLINE_HOURS = 720

const toHoursField = (value: number | null) => (value ? String(value) : '')

// Пустое поле — общий срок площадки, поэтому в null ошибки нет
const getHoursError = (value: string) => {
  if (!value.trim()) return ''

  const hours = Number(value)
  if (
    !Number.isInteger(hours) ||
    hours < MIN_DEADLINE_HOURS ||
    hours > MAX_DEADLINE_HOURS
  ) {
    return `Укажите целое число от ${MIN_DEADLINE_HOURS} до ${MAX_DEADLINE_HOURS}`
  }
  return ''
}

const toHoursValue = (value: string) => (value.trim() ? Number(value) : null)

export const SellerSettings: React.FC<ISellerSettingsProps> = ({ sellerId }) => {
  const [settings, setSettings] = React.useState<IAdminSellerSettings | null>(null)
  const [seller, setSeller] = React.useState<ISeller | null>(null)
  const [activity, setActivity] = React.useState<IAdminSellerActivity | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isPenaltySaving, setIsPenaltySaving] = React.useState(false)

  const [isFreeDeliveryDisabled, setIsFreeDeliveryDisabled] = React.useState(false)
  const [skipDeadlines, setSkipDeadlines] = React.useState(false)
  const [excludeFromAnalytics, setExcludeFromAnalytics] = React.useState(false)
  const [confirmationHours, setConfirmationHours] = React.useState('')
  const [shippingHours, setShippingHours] = React.useState('')

  const applySettings = (data: IAdminSellerSettings) => {
    setSettings(data)
    setIsFreeDeliveryDisabled(!data.isFreeDeliveryToPvz)
    setSkipDeadlines(data.skip_order_deadlines)
    setExcludeFromAnalytics(Boolean(data.exclude_from_analytics))
    setConfirmationHours(toHoursField(data.order_confirmation_deadline_hours))
    setShippingHours(toHoursField(data.order_shipping_deadline_hours))
  }

  const getSettings = async () => {
    try {
      setIsLoading(true)
      const [settingsResponse, sellerResponse, activityResponse] = await Promise.all([
        sellerApi.getSellerSettings(sellerId),
        sellerApi.getSeller(sellerId),
        // Справочный блок: его ошибка не должна оставлять страницу без настроек
        sellerApi.getSellerActivity(sellerId).catch(() => null)
      ])
      applySettings(settingsResponse.data)
      setSeller(sellerResponse.data)
      setActivity(activityResponse?.data ?? null)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getSettings()
  }, [sellerId])

  const confirmationError = getHoursError(confirmationHours)
  const shippingError = getHoursError(shippingHours)

  const hasChanges = settings
    ? isFreeDeliveryDisabled !== !settings.isFreeDeliveryToPvz ||
      skipDeadlines !== settings.skip_order_deadlines ||
      excludeFromAnalytics !== Boolean(settings.exclude_from_analytics) ||
      toHoursValue(confirmationHours) !== settings.order_confirmation_deadline_hours ||
      toHoursValue(shippingHours) !== settings.order_shipping_deadline_hours
    : false

  const onSubmit = async () => {
    if (confirmationError || shippingError) return

    try {
      setIsSaving(true)
      const { data } = await sellerApi.updateSellerSettings(sellerId, {
        isFreeDeliveryToPvz: !isFreeDeliveryDisabled,
        skip_order_deadlines: skipDeadlines,
        exclude_from_analytics: excludeFromAnalytics,
        order_confirmation_deadline_hours: toHoursValue(confirmationHours),
        order_shipping_deadline_hours: toHoursValue(shippingHours)
      })
      applySettings(data)
      showMessage('success', 'Настройки продавца сохранены', '')
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  // Бэкенд при включении обнуляет отмены и надбавку — повторяем это в карточке без перезапроса
  const saveSkipCancelPenalty = async (skip_cancel_penalty: boolean) => {
    try {
      setIsPenaltySaving(true)
      const { data } = await sellerApi.updateSellerSettings(sellerId, { skip_cancel_penalty })
      setSettings((prev) => prev && { ...prev, skip_cancel_penalty: data.skip_cancel_penalty })
      if (data.skip_cancel_penalty) {
        setSeller((prev) => prev && { ...prev, cancel_count: 0, commission_penalty: 0 })
      }
      showMessage(
        'success',
        data.skip_cancel_penalty ? 'Штрафы за отмены отключены' : 'Штрафы за отмены включены',
        ''
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsPenaltySaving(false)
    }
  }

  const onSkipCancelPenaltyChange = (checked: boolean) => {
    const hasCancels = Boolean(seller?.cancel_count || seller?.commission_penalty)

    if (!checked || !hasCancels) {
      saveSkipCancelPenalty(checked)
      return
    }

    Modal.confirm({
      centered: true,
      title: 'Не штрафовать продавца за отмены?',
      content: 'Счётчик отмен и надбавка к комиссии за них обнулятся.',
      okText: 'Не штрафовать',
      cancelText: 'Отмена',
      onOk: () => saveSkipCancelPenalty(checked)
    })
  }

  if (isLoading) {
    return <Skeleton active />
  }

  return (
    <div>
      <SectionTitle level={2}>Настройки продавца</SectionTitle>
      {activity && <SellerActivity activity={activity} />}
      <Typography.Text className={styles.hint}>
        Эти настройки меняет только админ площадки — в кабинете продавца этого раздела нет.
      </Typography.Text>

      <div className={styles.toggles}>
        <div className={styles.toggleRow}>
          <Switch
            checked={isFreeDeliveryDisabled}
            onChange={setIsFreeDeliveryDisabled}
          />
          <Typography.Text>Отключить бесплатную доставку</Typography.Text>
        </div>
        <div className={styles.toggleRow}>
          <Switch checked={skipDeadlines} onChange={setSkipDeadlines} />
          <Typography.Text>Не отменять просроченные заказы</Typography.Text>
        </div>
        <div className={styles.toggleRow}>
          <Switch checked={excludeFromAnalytics} onChange={setExcludeFromAnalytics} />
          <Typography.Text>Исключить из аналитики</Typography.Text>
        </div>
      </div>

      <div className={styles.deadlines}>
        <div className={styles.deadlineField}>
          <Typography.Text className={styles.deadlineLabel}>
            Срок на подтверждение заказа, часов
          </Typography.Text>
          <Input
            value={confirmationHours}
            disabled={skipDeadlines}
            status={confirmationError ? 'error' : undefined}
            placeholder={`Срок площадки — ${ORDER_CONFIRMATION_DEADLINE_HOURS}`}
            onChange={(e) => setConfirmationHours(e.target.value.replace(/\D/g, ''))}
          />
          <Typography.Text type='danger' className={styles.deadlineError}>
            {confirmationError}
          </Typography.Text>
        </div>
        <div className={styles.deadlineField}>
          <Typography.Text className={styles.deadlineLabel}>
            Срок на отправку заказа, часов
          </Typography.Text>
          <Input
            value={shippingHours}
            disabled={skipDeadlines}
            status={shippingError ? 'error' : undefined}
            placeholder={`Срок площадки — ${ORDER_SHIPPING_DEADLINE_HOURS}`}
            onChange={(e) => setShippingHours(e.target.value.replace(/\D/g, ''))}
          />
          <Typography.Text type='danger' className={styles.deadlineError}>
            {shippingError}
          </Typography.Text>
        </div>
      </div>

      {hasChanges && (
        <div className={styles.footer}>
          <Button
            type='primary'
            loading={isSaving}
            disabled={Boolean(confirmationError || shippingError)}
            onClick={onSubmit}
          >
            Сохранить
          </Button>
        </div>
      )}

      {seller && (
        <div className={styles.commission}>
          <SellerCommission
            sellerId={sellerId}
            currentCommission={seller.commission}
            onSuccess={(commission) => setSeller((prev) => prev && { ...prev, commission })}
          />
          <div>
            <div className={styles.penaltyToggle}>
              <Switch
                checked={Boolean(settings?.skip_cancel_penalty)}
                loading={isPenaltySaving}
                onChange={onSkipCancelPenaltyChange}
              />
              <Typography.Text>Не штрафовать за отмены</Typography.Text>
            </div>
            <SellerCancelPenalty
              cancelCount={seller.cancel_count ?? 0}
              commissionPenalty={seller.commission_penalty ?? 0}
              isDisabledForCancels={Boolean(
                seller.disabled_reasons?.includes(ESellerDisableReason.CANCELS)
              )}
              disabledUntil={seller.disabled_for_cancels_until}
              isExempt={Boolean(settings?.skip_cancel_penalty)}
            />
          </div>
          <SellerCommissionDiscount
            sellerId={sellerId}
            currentCommission={seller.commission}
            discount={seller.commission_discount}
            onSuccess={(commission_discount) =>
              setSeller((prev) => prev && { ...prev, commission_discount })
            }
          />
        </div>
      )}
    </div>
  )
}
